import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { checkVillasAvailability, VILLA_SLUGS } from '@/lib/availability';

export interface VillaAvailabilityResult {
  available: Set<string>;
  checkIn: string;
  checkOut: string;
}

interface AvailabilitySearchBarProps {
  villaSlug?: string;
  onMultiResult?: (available: Set<string>, checkIn: string, checkOut: string) => void;
}

export default function AvailabilitySearchBar(props: AvailabilitySearchBarProps) {
  const { villaSlug, onMultiResult } = props;
  const { t } = useTranslation();

  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState('2');
  const [guestOpen, setGuestOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<'available' | 'occupied' | null>(null);

  const today = new Date().toISOString().split('T')[0];
  const isSingle = Boolean(villaSlug);

  function scrollToBooking() {
    const el = document.getElementById('booking');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function handleSearch() {
    setError(null);
    setResult(null);

    if (!checkIn || !checkOut) {
      setError(t('search_error_dates'));
      return;
    }
    if (checkOut <= checkIn) {
      setError(t('search_error_order'));
      return;
    }

    setChecking(true);
    try {
      const slugs = isSingle ? [villaSlug as string] : [...VILLA_SLUGS];
      const available = await checkVillasAvailability(checkIn, checkOut, slugs);

      if (isSingle) {
        setResult(available.size > 0 ? 'available' : 'occupied');
      } else {
        onMultiResult?.(available, checkIn, checkOut);
        const el = document.getElementById('villas');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch {
      setError(t('search_error_generic'));
    } finally {
      setChecking(false);
    }
  }

  return (
    <section id="search" className="relative z-20 -mt-10 px-4 md:px-10">
      <div className="max-w-4xl mx-auto">
        <div className="bg-background-50 rounded-lg p-5 md:p-6">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-stretch md:items-end">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-1.5">
                {t('search_check_in')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-calendar-line text-sm"></i>
                </span>
                <input
                  type="date"
                  value={checkIn}
                  min={today}
                  onChange={(e) => setCheckIn(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200 cursor-pointer"
                />
              </div>
            </div>

            <div className="flex-1">
              <label className="block text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-1.5">
                {t('search_check_out')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                  <i className="ri-calendar-line text-sm"></i>
                </span>
                <input
                  type="date"
                  value={checkOut}
                  min={checkIn || today}
                  onChange={(e) => setCheckOut(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200 cursor-pointer"
                />
              </div>
            </div>

            <div className="flex-1 relative">
              <label className="block text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-1.5">
                {t('search_guests')}
              </label>
              <button
                type="button"
                onClick={() => setGuestOpen(!guestOpen)}
                className="w-full flex items-center justify-between pl-3 pr-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 hover:border-primary-400 transition-all duration-200 cursor-pointer"
              >
                <span>
                  {guests}{' '}
                  {parseInt(guests) === 1 ? t('search_guest_singular') : t('search_guest_plural')}
                </span>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i
                    className={`text-xs transition-transform duration-200 ${
                      guestOpen ? 'rotate-180' : ''
                    } ri-arrow-down-s-line`}
                  ></i>
                </span>
              </button>
              {guestOpen && (
                <div className="absolute top-full mt-1 left-0 right-0 bg-background-50 border border-background-200 rounded-md shadow-lg z-30 py-1">
                  {[1, 2, 3, 4].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => {
                        setGuests(String(n));
                        setGuestOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap ${
                        parseInt(guests) === n
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-foreground-700'
                      }`}
                    >
                      {n} {n === 1 ? t('search_guest_singular') : t('search_guest_plural')}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="md:self-end">
              <button
                type="button"
                onClick={handleSearch}
                disabled={checking}
                className="w-full md:w-auto bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-md text-sm font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer flex items-center justify-center gap-2"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  {checking ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    <i className="ri-search-line"></i>
                  )}
                </span>
                {checking ? t('search_checking') : t('search_search')}
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-4 flex items-center gap-2 bg-accent-50 text-accent-800 px-3 py-2 rounded-md">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-error-warning-line text-sm"></i>
              </span>
              <span className="text-xs font-medium">{error}</span>
            </div>
          )}

          {result === 'available' && (
            <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-accent-50 border border-accent-200 text-accent-900 px-4 py-3 rounded-md">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-lg"></i>
                </span>
                <span className="text-sm font-medium">{t('search_available_single')}</span>
              </div>
              <button
                type="button"
                onClick={scrollToBooking}
                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
              >
                {t('search_book_now')}
              </button>
            </div>
          )}

          {result === 'occupied' && (
            <div className="mt-4 flex items-center gap-2 bg-accent-50 border border-accent-200 text-accent-800 px-4 py-3 rounded-md">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-close-circle-line text-sm"></i>
              </span>
              <span className="text-sm font-medium">{t('search_not_available_single')}</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 pt-4 border-t border-background-200 gap-3">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-accent-600">
                <i className="ri-checkbox-circle-fill text-sm"></i>
              </span>
              <span className="text-xs md:text-sm text-foreground-600 font-medium whitespace-nowrap">
                {t('search_free_cancel')}
              </span>
            </div>
            <div className="flex items-center gap-2 bg-accent-50 text-accent-800 px-3 py-1.5 rounded-md">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-alert-line text-sm"></i>
              </span>
              <span className="text-xs font-medium whitespace-nowrap">{t('search_urgency')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}